import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { can, type LoginResponse, type SessionUser } from '@amai/contracts';
import { api } from './api.js';
import {
  clearTokens,
  ESPERAS_AO_CONFIRMAR,
  hasSession,
  sessaoEncerrada,
  setTokens,
} from './session.js';
import { AuthContext, type AuthState } from './auth-context.js';

/**
 * Sessão do operador.
 *
 * Os tokens ficam em `session.ts`; aqui mora o estado do usuário logado e as
 * permissões efetivas usadas para esconder ações que ele não pode executar.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<AuthState['status']>(hasSession() ? 'loading' : 'anonymous');
  // Muda a cada "Tentar de novo": é o que reinicia o efeito abaixo.
  const [tentativa, setTentativa] = useState(0);

  // Retoma a sessão ao abrir/recarregar: os tokens sobrevivem ao F5.
  //
  // Só um `401` encerra a sessão. Antes, QUALQUER falha do `/auth/me`
  // apagava os tokens: um F5 no pico de uso (429), com a API reiniciando
  // num deploy (502) ou com a rede oscilando mandava o operador de volta
  // para o login, e ele digitava a senha de novo por nada.
  useEffect(() => {
    // Sem token guardado o estado inicial já é 'anonymous': nada a fazer.
    if (!hasSession()) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const confirmar = (rodada: number) => {
      api
        .get<SessionUser>('/auth/me')
        .then((me) => {
          if (!alive) return;
          setUser(me);
          setStatus('authenticated');
        })
        .catch((error: unknown) => {
          if (!alive) return;
          if (sessaoEncerrada(error)) {
            clearTokens();
            setStatus('anonymous');
            return;
          }
          const espera = ESPERAS_AO_CONFIRMAR[rodada];
          if (espera === undefined) {
            setStatus('unreachable');
            return;
          }
          timer = setTimeout(() => confirmar(rodada + 1), espera);
        });
    };

    confirmar(0);

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [tentativa]);

  const retrySession = useCallback(() => {
    setStatus('loading');
    setTentativa((n) => n + 1);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await api.post<LoginResponse>(
      '/auth/login',
      { email, password },
      { skipRefresh: true },
    );
    setTokens(response.tokens);
    setUser(response.user);
    setStatus('authenticated');
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Sessão já inválida do lado do servidor: sair localmente basta.
    }
    clearTokens();
    setUser(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      status,
      signIn,
      signOut,
      retrySession,
      can: (code) =>
        user ? can({ fullAccess: user.fullAccess, permissions: user.permissions }, code) : false,
    }),
    [user, status, signIn, signOut, retrySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
